import {
  transcriptionAnnotations,
  translationAnnotations,
  translationFor,
} from "../data/loader.js";
import type { Language, PageData } from "../data/types.js";
import { LANG_LABEL, type ReadingTab } from "../lib/i18n.js";
import { t } from "../lib/strings.js";
import { AnnotationLayer } from "./AnnotationLayer.js";

interface Props {
  page: PageData;
  lang: Language;
  tab: ReadingTab;
  onTabChange: (tab: ReadingTab) => void;
}

export function TextPanes({ page, lang, tab, onTabChange }: Props): JSX.Element {
  const s = t(lang);
  const transcription = page.transcription;
  const translation = translationFor(page, lang);
  const txAnns = transcriptionAnnotations(page);
  const trAnns = translationAnnotations(page, lang);

  return (
    <div className="flex flex-col lg:h-full bg-white border border-parchment-200 rounded-md overflow-hidden">
      <div className="flex items-center border-b border-parchment-200 text-sm">
        <button
          type="button"
          className={`px-4 py-2 flex-1 ${
            tab === "translation"
              ? "bg-parchment-100 font-semibold"
              : "text-ink-muted hover:bg-parchment-50"
          }`}
          onClick={() => onTabChange("translation")}
        >
          {LANG_LABEL[lang]}
        </button>
        <button
          type="button"
          className={`px-4 py-2 flex-1 border-l border-parchment-200 ${
            tab === "transcription"
              ? "bg-parchment-100 font-semibold"
              : "text-ink-muted hover:bg-parchment-50"
          }`}
          onClick={() => onTabChange("transcription")}
        >
          {s.originalTab}
          {transcription && transcription.sourceLanguages.length > 0 && (
            <span className="ml-1 text-xs text-ink-muted">
              ({transcription.sourceLanguages.join(",").toUpperCase()})
            </span>
          )}
        </button>
      </div>
      <div className="flex-1 lg:overflow-y-auto p-4">
        {tab === "translation" ? (
          translation?.text ? (
            <div className="prose-diary">
              <AnnotationLayer text={translation.text} annotations={trAnns} lang={lang} />
            </div>
          ) : translation?.status === "skipped_same_language" ? (
            <div className="text-ink-muted text-sm italic">
              {s.originalIsInTargetLanguage(LANG_LABEL[lang])}
            </div>
          ) : translation?.status === "failed" ? (
            <div className="text-red-700 text-sm">{s.translationFailed("")}</div>
          ) : (
            <div className="text-ink-muted text-sm italic">{s.noTranslation}</div>
          )
        ) : transcription?.text ? (
          <div className="prose-diary">
            <AnnotationLayer text={transcription.text} annotations={txAnns} lang={lang} />
          </div>
        ) : (
          <div className="text-ink-muted text-sm italic">{s.noTranscription}</div>
        )}
      </div>
      {(page.page.labelOriginal || page.page.authorLabel) && (
        <div className="border-t border-parchment-200 px-4 py-1.5 text-xs text-ink-muted bg-parchment-50">
          {s.msPageLabel}: {page.page.labelOriginal ?? "—"}
          {page.page.authorLabel && (
            <span className="ml-2">
              — {s.authorPageLabel}: {page.page.authorLabel}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
